import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useSystemTheme } from '../hooks/useSystemTheme'

type Theme = 'light' | 'dark' | 'system'
type ActualTheme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  actualTheme: ActualTheme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Must match the inline FOIT-prevention script in `index.html`.
const THEME_STORAGE_KEY = 'buildtory-theme'
const LEGACY_THEME_STORAGE_KEY = 'realassets-theme'

// Migrate legacy theme values (unicorn/midnight/halloween) to their closest
// supported counterpart so existing users are not stuck on a removed theme.
function normalizeStoredTheme(value: string | null): Theme {
  if (value === 'light' || value === 'dark' || value === 'system') return value
  if (value === 'midnight' || value === 'halloween') return 'dark'
  if (value === 'unicorn') return 'light'
  return 'system'
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const systemTheme = useSystemTheme()
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system'
    try {
      // Read current key; fall back to the legacy key for migration.
      const raw =
        localStorage.getItem(THEME_STORAGE_KEY) ??
        localStorage.getItem(LEGACY_THEME_STORAGE_KEY)
      return normalizeStoredTheme(raw)
    } catch {
      return 'system'
    }
  })

  const actualTheme: ActualTheme = theme === 'system' ? systemTheme : theme

  // The theme cross-fade is opt-in: `.theme-transitioning` is what turns the
  // 300 ms colour transition on (see `index.css`). It is stamped only when the
  // resolved theme actually CHANGES, never on first paint — otherwise the page
  // would fade in from the wrong colours on every load.
  const previousTheme = useRef<ActualTheme | null>(null)

  useEffect(() => {
    const root = window.document.documentElement
    // Clean up any legacy theme classes as well
    root.classList.remove('light', 'dark', 'unicorn', 'halloween', 'midnight')
    root.classList.add(actualTheme)
    root.setAttribute('data-theme', actualTheme)

    const changed = previousTheme.current !== null && previousTheme.current !== actualTheme
    previousTheme.current = actualTheme
    if (!changed) return

    root.classList.add('theme-transitioning')
    const timer = window.setTimeout(() => {
      root.classList.remove('theme-transitioning')
    }, 300)
    return () => {
      window.clearTimeout(timer)
      root.classList.remove('theme-transitioning')
    }
  }, [actualTheme])

  const setTheme = (newTheme: Theme) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    } catch {
      // Handle storage errors gracefully
    }
    setThemeState(newTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, actualTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
